import { zip, AsyncZippable, Zip, ZipPassThrough } from "fflate";
import { IAttachmentFile } from "../models/attachmentFile.ts";

/**
 * CSV ファイルと添付ファイルを ZIP アーカイブにまとめる関数（非同期）
 *
 * 非同期処理によりメインスレッドのブロックを回避し、大量ファイルでも
 * 「ページが応答しません」エラーを防止します。
 * 添付ファイルは既に圧縮済み（PDF/JPG/PNG等）が大半のため、圧縮レベル0（無圧縮）で処理します。
 *
 * @param {Uint8Array} csvFile - ZIP に含める CSV ファイルのバイナリデータ（Shift_JIS エンコード済み）
 * @param {string} csvFileName - CSV ファイル名
 * @param {IAttachmentFile[]} attachmentFiles - ZIP に含める添付ファイルのリスト
 * @returns {Promise<Uint8Array>} - ZIP のバイナリデータ
 */
export const createZip = (
  csvFile: Uint8Array,
  csvFileName: string,
  attachmentFiles: IAttachmentFile[],
): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const zippableFiles: AsyncZippable = {};

    // 圧縮レベル0（無圧縮）: 添付ファイルは既に圧縮済みのためCPU負荷を回避
    zippableFiles[csvFileName] = [csvFile, { level: 0 }];
    for (const file of attachmentFiles) {
      zippableFiles[file.name] = [file.content, { level: 0 }];
    }

    zip(zippableFiles, (err, data) => {
      if (err) {
        reject(err);
      } else {
        resolve(data);
      }
    });
  });
};

/**
 * ストリーミング方式でZIPを構築するビルダー
 *
 * ファイルを1つずつ追加し、追加済みデータはメモリから解放される。
 * 大量の添付ファイルがある場合にメモリ使用量を抑えられる。
 */
export class StreamingZipBuilder {
  private chunks: Uint8Array[] = [];
  private zip: Zip;

  constructor() {
    this.zip = new Zip((err, chunk, _final) => {
      if (err) throw err;
      this.chunks.push(chunk);
    });
  }

  addFile(name: string, content: Uint8Array): void {
    const file = new ZipPassThrough(name);
    this.zip.add(file);
    file.push(content, true);
  }

  finish(): Uint8Array {
    this.zip.end();

    // チャンクを結合
    const totalLength = this.chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of this.chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    this.chunks = [];
    return result;
  }
}
